import Splide from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

const SELECTORS = {
    block: '.wp-block-ssm-logo-wall',
    carousel: '.splide',
    list: '.splide__list',
    track: '.splide__track',
};

const DEFAULT_OPTIONS = {
    type: 'loop',
    arrows: false,
    pagination: false,
    gap: '2rem',
    autoWidth: true,
    drag: false,
    waitForTransition: false,
    speed: 0.1,
    autoScroll: {
        speed: 1.1,
        pauseOnHover: false,
        pauseOnFocus: false,
    },
};

export function initLogoWall(block) {
    const splideEl = block.querySelector(SELECTORS.carousel);
    const list = splideEl?.querySelector(SELECTORS.list);
    const track = splideEl?.querySelector(SELECTORS.track);

    // Skip if already initialized or missing required elements
    if (!splideEl || !list || !track || splideEl.splide) {
        return null;
    }

    try {
        let sliderInstance = null;
        const eventCleanup = [];

        const toggleSlider = () => {
            try {
                // Measure in nowrap mode
                splideEl.classList.remove('logo-wall--static');
                list.style.flexWrap = 'nowrap';
                const shouldSlide = list.scrollWidth > track.clientWidth + 1;

                if (shouldSlide) {
                    splideEl.classList.remove('logo-wall--static');
                    splideEl.style.visibility = 'visible';

                    if (!sliderInstance) {
                        sliderInstance = new Splide(splideEl, DEFAULT_OPTIONS);
                        sliderInstance.mount({ AutoScroll });
                    } else {
                        sliderInstance.refresh();
                    }
                } else {
                    if (sliderInstance) {
                        sliderInstance.destroy(true);
                        sliderInstance = null;
                    }
                    splideEl.classList.add('logo-wall--static');
                    splideEl.style.visibility = 'visible';
                }
            } catch (error) {
                console.error('Error toggling logo wall slider:', error);
            }
        };

        // Wait for images to load before initializing
        const initAfterImagesLoad = () => {
            const imgs = Array.from(list.querySelectorAll('img'));

            if (imgs.length === 0) {
                toggleSlider();

                return;
            }

            let loaded = 0;
            const totalImages = imgs.length;

            const checkAllLoaded = () => {
                loaded += 1;
                if (loaded === totalImages) {
                    toggleSlider();
                }
            };

            imgs.forEach((img) => {
                if (img.complete) {
                    checkAllLoaded();
                } else {
                    img.addEventListener('load', checkAllLoaded);
                    img.addEventListener('error', () => {
                        console.warn('Logo image failed to load:', img.src);
                        checkAllLoaded(); // Still count it so we don't hang
                    });
                }
            });
        };

        initAfterImagesLoad();

        // Add resize handler
        const resizeHandler = () => toggleSlider();
        window.addEventListener('resize', resizeHandler);
        eventCleanup.push(() => window.removeEventListener('resize', resizeHandler));

        // Store reference for cleanup
        splideEl.splide = true;

        return {
            destroy() {
                eventCleanup.forEach((cleanup) => cleanup());
                if (sliderInstance && typeof sliderInstance.destroy === 'function') {
                    sliderInstance.destroy(true);
                    sliderInstance = null;
                }
                delete splideEl.splide;
            },
        };
    } catch (error) {
        console.error('Error initializing logo wall:', error, splideEl);

        return null;
    }
}

export function LogoWall() {
    document.querySelectorAll(SELECTORS.block).forEach((block) => {
        initLogoWall(block);
    });
}
